import { Alert, TextLink, Text, Box } from 'braid-design-system';
import { DateTime } from 'luxon';

import { ContractStatus } from 'src/generated/graphql';
import type { ClientListingForListingsTab } from 'src/types/graphql-derived';
import {
  ConsoleErrorId,
  consoleErrorWrapper,
  createPackagesUrlWithListingId,
  createPostListingUrl,
} from 'src/utils';

// Show base contract has expired alert when it has been expired only within the next 186 days (approximately 6 months)
const SHOW_BASE_CONTRACT_EXPIRED_ALERT_THRESHOLD_DAYS = 186;

const numberOfDaysBetweenDates = (startDate: Date, endDate: Date) =>
  Math.ceil(
    DateTime.fromJSDate(endDate).diff(DateTime.fromJSDate(startDate), 'days')
      .days,
  );

export interface AdAlertNotificationProps {
  listing: ClientListingForListingsTab;
  hasActiveListings: boolean;
  hasPaidContract: boolean;
}

const AdAlertNotificationExpired = ({
  listing,
  hasActiveListings,
  hasPaidContract,
}: AdAlertNotificationProps) => {
  if (listing.baseContract === null) {
    return null;
  }

  if (listing.baseContract.status !== ContractStatus.Expired) {
    // Only handle Expired status in this component
    return null;
  }

  if (!listing.baseContract.expiredDate) {
    // Check for data corruption - if contract status is Expired, expiredDate must be present
    consoleErrorWrapper(
      ConsoleErrorId.AdAlertNotificationBaseContractValidation,
      `Listing ID ${listing.id} has base contract with Expired status but missing expiredDate`,
    );
    return null;
  }

  const expiredSinceInDays = numberOfDaysBetweenDates(
    new Date(listing.baseContract.expiredDate),
    new Date(),
  );

  const listingBaseContractExpiredDate = new Date(
    listing.baseContract.expiredDate,
  ).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  /**
   * Show base contract has expired warning alert when:
   * - base contract status is Expired
   * - expired date is within threshold of 186 days (approximately 6 months) from today
   * - there are no other active listings for the client
   */
  const showListingBaseContractHasExpiredWarningAlert = Boolean(
    expiredSinceInDays <= SHOW_BASE_CONTRACT_EXPIRED_ALERT_THRESHOLD_DAYS &&
      !hasActiveListings,
  );
  /**
   * URL for repost link in expired alert
   * - If user has a paid contract, link to CASBA preview page
   * - If user does not have a paid contract, link to packages page to buy a contract
   */
  const urlForRepostLink = hasPaidContract
    ? createPostListingUrl(listing.clientId, listing.id)
    : createPackagesUrlWithListingId(listing.id);

  if (showListingBaseContractHasExpiredWarningAlert) {
    return (
      <Box data-testid="ad-alert-expired-notification-container">
        <Alert tone="caution">
          <Text>
            Your {listing.baseContract.productTitle} ad expired on{' '}
            {listingBaseContractExpiredDate}.{' '}
            <TextLink
              href={urlForRepostLink}
              aria-label="private seller dashboard repost for expired listing url"
            >
              Repost
            </TextLink>{' '}
            to continue advertising.
          </Text>
        </Alert>
      </Box>
    );
  }

  return null;
};

export default AdAlertNotificationExpired;
